// src/pages/profile-settings/patient/EditIdPassport.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/sot/primitives/TopBar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import ConsentModal from "@/components/ConsentModal";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import supabase from "@/services/supabaseClient";
import { isPassportNumber } from "@/utils/id-validators";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { PATIENT_SETTINGS_PROFILE_ROUTE } from "@/pages/profile-settings/settingsNavigation.js";
import {
  resolveCanonicalKycState,
  resolveUploadId,
  subscribeToUploadStatus,
  uploadDocumentForOcr,
} from "@/lib/idVerificationFlow";

import {
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";
import {
  selectPatientIdentityHydration,
  sanitizeIdentityOcrFields,
} from "@/pages/shared/identityHydrationSelectors.js";
import IdentityStatusCard from "@/pages/shared/components/IdentityStatusCard.jsx";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";
import { signedUrl } from "@/lib/storage.js";

// Same light MRZ shape checks we use in onboarding
const MRZ_LINE = /^[A-Z0-9<]{30}$/;
const MRZ_3 = /^[A-Z0-9<]{30}\n[A-Z0-9<]{30}\n[A-Z0-9<]{30}$/;
const looksLikeMrz = (s = "") => {
  const t = String(s).trim().toUpperCase();
  return MRZ_LINE.test(t) || MRZ_3.test(t);
};

// Zimbabwe-style National ID: e.g. "89 017 178 G41"
const ZW_ID = /^[0-9]{2}\s?[0-9]{3}\s?[0-9]{3}\s?[A-Z][0-9]{2}$/;
const looksLikeZwId = (s = "") => ZW_ID.test(String(s).toUpperCase());

function parseUploadNotes(notes) {
  if (!notes) return null;
  if (typeof notes === "object") return notes;
  try {
    return JSON.parse(String(notes));
  } catch {
    const trimmed = String(notes || "").trim();
    if (trimmed.startsWith("{")) {
      const firstLine = trimmed.split(/\r?\n/, 1)[0]?.trim();
      if (firstLine && firstLine !== trimmed) {
        try {
          return JSON.parse(firstLine);
        } catch {
          // Fall through to null.
        }
      }
    }
    return null;
  }
}

function formatDateDdMmYyyy(raw = "") {
  const s = String(raw || "").trim();
  if (!s) return "";

  let m = s.match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;

  m = s.match(/^(\d{4})[./-](\d{2})[./-](\d{2})(?:[T\s].*)?$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;

  m = s.match(/^([0-3]?\d)\s+([A-Za-z]{3,9})[,\s]+(\d{4})$/);
  if (m) {
    const months = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];
    const monthIdx = months.indexOf(String(m[2]).slice(0, 3).toUpperCase());
    if (monthIdx >= 0) {
      const dd = String(Number(m[1])).padStart(2, "0");
      const mm = String(monthIdx + 1).padStart(2, "0");
      return `${dd}-${mm}-${m[3]}`;
    }
  }

  return s;
}

function toOfficialText(raw = "") {
  const s = String(raw || "").trim();
  return s ? s.toUpperCase() : "";
}

function hasProtectedVerificationEvidence(rows = []) {
  return (Array.isArray(rows) ? rows : []).some((row) => {
    const status = String(row?.status || "").toLowerCase().trim();
    const provider = String(row?.provider || "").toLowerCase().trim();
    const details = row?.details && typeof row.details === "object" ? row.details : {};
    const registrySource = String(details?.registry_source || details?.registrySource || "").trim();
    const protectedSource = String(details?.protected_source || details?.protectedSource || "").trim();
    const note = String(details?.note || "").toLowerCase();
    if (!["verified", "approved"].includes(status)) return false;
    if (note.includes("not registry verified")) return false;
    return Boolean(
      registrySource ||
        protectedSource ||
        provider.includes("registry") ||
        provider.includes("protected")
    );
  });
}

function identityDocumentStateLabel(status, fallback = "Pending protected review") {
  const normalized = String(status || "").toLowerCase().trim();
  if (["verified", "approved"].includes(normalized)) return "Verified";
  if (normalized === "admin_reviewed") return "Pending protected review";
  if (["ocr_done", "done", "passed"].includes(normalized)) return "Recognised";
  if (["failed", "rejected", "requires_attention"].includes(normalized)) return "Requires attention";
  return fallback;
}

function identityDocumentStateClass(status) {
  const normalized = String(status || "").toLowerCase().trim();
  const base =
    "inline-flex min-h-5 items-center rounded-full px-2 py-0.5 text-label-medium font-medium leading-4 whitespace-nowrap";
  if (["verified", "approved", "ocr_done", "done", "passed"].includes(normalized)) {
    return `${base} bg-[rgba(0,189,196,0.12)] text-[#008A8F]`;
  }
  if (["failed", "rejected", "requires_attention"].includes(normalized)) {
    return `${base} bg-[var(--state-critical-12)] text-[rgb(var(--schemes-critical))]`;
  }
  return `${base} bg-[rgba(86,106,139,0.08)] text-on-surface-var`;
}

function resolveIdentityDocumentChipState({
  uploadStatus,
  ocrStatus,
  verifyStatus,
  hasOcrEvidence,
}) {
  const normalizedUpload = String(uploadStatus || "").toLowerCase().trim();
  const normalizedOcr = String(ocrStatus || "").toLowerCase().trim();
  const normalizedVerify = String(verifyStatus || "").toLowerCase().trim();

  if (["verified", "approved"].includes(normalizedUpload)) return normalizedUpload;
  if (["failed", "rejected", "requires_attention"].includes(normalizedUpload)) {
    return normalizedUpload;
  }
  if (
    hasOcrEvidence ||
    ["ocr_done", "done", "passed"].includes(normalizedUpload) ||
    normalizedOcr === "done" ||
    normalizedVerify === "passed"
  ) {
    return "ocr_done";
  }

  return normalizedUpload || normalizedOcr || normalizedVerify || "";
}

export default function EditIdPassport() {
  const nav = useNavigate();
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const { t, formatDate } = useLanguage();
  const { isDark } = useTheme();
  const [value, setValue] = React.useState("");
  const [consentOpen, setConsentOpen] = React.useState(false);
  const [captured, setCaptured] = React.useState(null); // File | null
  const [pendingAction, setPendingAction] = React.useState(null); // camera | file | null
  const [uploadMeta, setUploadMeta] = React.useState(null); // { storage_path, retention_expires_at } | null
  const [existingIdentityDocUrl, setExistingIdentityDocUrl] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [verifyStatus, setVerifyStatus] = React.useState("idle");
  const [verifyMsg, setVerifyMsg] = React.useState("");
  const [ocrStatus, setOcrStatus] = React.useState("idle");
  const [ocrMsg, setOcrMsg] = React.useState("");
  const [ocrSummary, setOcrSummary] = React.useState(null);
  const [identityVerified, setIdentityVerified] = React.useState(false);
  const [hydrationDebug, setHydrationDebug] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const fileRef = React.useRef(null);
  const activeUploadIdRef = React.useRef(null);

  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
  });

  // Country-aware format check (keep generic for now)
const selectedCountry = "generic";
const v = String(value || "").toUpperCase();
const valid =
  isPassportNumber(selectedCountry, v) ||
  looksLikeMrz(v) ||
  looksLikeZwId(v); // <- allow Zim ID like "89 017 178 G41"
const canSave = (valid || ocrStatus === "done") && !busy;
const showVerified = valid || ocrStatus === "done";

const uiState =
  identityVerified
    ? "verified"
    : ocrStatus === "failed" || verifyStatus === "failed"
    ? "failed"
    : ocrStatus === "done"
      ? "done"
      : ocrStatus === "pending" || verifyStatus === "pending"
        ? "pending"
        : verifyStatus === "passed"
          ? "received"
          : "idle";

const statusTitle =
  uiState === "verified"
    ? t("onboarding.idPassport.status.verified", "Verified")
    : uiState === "pending"
    ? t("onboarding.idPassport.status.pending", "Analysing Document...")
    : uiState === "done"
      ? t("onboarding.idPassport.status.ocrCompleted", "OCR complete. Review pending.")
      : uiState === "failed"
        ? t("onboarding.idPassport.status.failed", "Processing Failed")
        : uiState === "received"
          ? t("onboarding.idPassport.status.received", "Document Received")
          : t("onboarding.idPassport.status.idle", "Processing...");

const panelTone =
  uiState === "done" || uiState === "verified"
    ? isDark
      ? "bg-[rgba(var(--afrimed-tertiary),0.18)] border-[rgba(var(--afrimed-tertiary),0.48)]"
      : "bg-[rgba(var(--afrimed-tertiary),0.16)] border-[rgba(var(--afrimed-tertiary),0.45)]"
    : uiState === "failed"
      ? "bg-[rgb(var(--schemes-error-container))] border-[rgb(var(--schemes-critical))]"
    : "bg-[rgba(var(--clr-on-surface),0.03)] border-[rgba(var(--clr-outline-variant),0.55)]";

const ocrFields = React.useMemo(() => {
  const base = sanitizeIdentityOcrFields(ocrSummary?.fields);
  if (!base.document_number && v) {
    base.document_number = v;
  }
  return base;
}, [ocrSummary, v]);

const ocrSurname =
  ocrFields.surname || ocrFields.last_name || ocrFields.family_name || "";
const ocrGivenNames =
  ocrFields.given_names ||
  ocrFields.first_name ||
  ocrFields.names ||
  ocrFields.full_name ||
  "";
const ocrDocNumber =
  ocrFields.document_number ||
  ocrFields.id_number ||
  ocrFields.id_no ||
  ocrFields.passport_number ||
  "";
const ocrDob = ocrFields.date_of_birth || ocrFields.dob || "";
const ocrExpiry = ocrFields.expiry_date || ocrFields.expiry || "";
const ocrDobDisplay = formatDateDdMmYyyy(ocrDob);
const ocrExpiryDisplay = formatDateDdMmYyyy(ocrExpiry);
const hasOcrEvidence = React.useMemo(
  () =>
    Object.values(ocrFields || {}).some((fieldValue) =>
      String(fieldValue || "").trim()
    ),
  [ocrFields]
);
const identityDocumentChipState = resolveIdentityDocumentChipState({
  uploadStatus: uploadMeta?.status,
  ocrStatus,
  verifyStatus,
  hasOcrEvidence,
});

React.useEffect(() => {
  if (typeof window === "undefined") return;
  window.__AFRIMED_ID_PASSPORT_DEBUG__ = {
    source: hydrationDebug,
    ocrFields,
    identityStatusCardFields: Object.keys(ocrFields).length > 0
      ? {
          surname: toOfficialText(ocrSurname) || "",
          document_number: toOfficialText(ocrDocNumber) || "",
          date_of_birth: ocrDobDisplay || "",
          expiry_date: ocrExpiryDisplay || "",
          given_names: toOfficialText(ocrGivenNames) || "",
        }
      : null,
  };
}, [hydrationDebug, ocrDobDisplay, ocrDocNumber, ocrExpiryDisplay, ocrFields, ocrGivenNames, ocrSurname]);

  function handleChange(next) {
    setValue(String(next || "").toUpperCase());
  }

  const triggerCapture = () => fileRef.current?.click();

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const preview = loadPatientIntroPreview() || {};
        const previewValue = String(preview?.nationalId || "").trim().toUpperCase();
        if (previewValue && alive) setValue(previewValue);

        const {
          data: { user },
          error: authErr,
        } = await supabase.auth.getUser();
        if (authErr || !user || !alive) return;

        const {
          profileId,
          profileStatus: status,
          latestUpload,
          latestStatus,
          docValue,
          hydratedFields,
          latestCheck,
          latestCheckFields,
          latestFields,
        } = await selectPatientIdentityHydration({
          supabase,
          userId: user.id,
        });
        const { data: protectedChecks } = await supabase
          .from("id_checks")
          .select("status,provider,details,checked_at")
          .eq("user_id", user.id)
          .order("checked_at", { ascending: false })
          .limit(10);
        const protectedVerified = hasProtectedVerificationEvidence(protectedChecks);

        if (!alive) return;

        if (docValue || profileId) {
          setValue((prev) =>
            String(prev || "").trim() ? prev : docValue || profileId
          );
        }

        if (
          latestUpload?.id ||
          latestUpload?.storage_path ||
          latestUpload?.path ||
          latestUpload?.retention_expires_at
        ) {
          const uploadPath = latestUpload?.storage_path || latestUpload?.path || null;
          setUploadMeta({
            upload_id: latestUpload?.id || null,
            path: uploadPath,
            status: latestUpload?.status || null,
            created_at: latestUpload?.created_at || null,
            updated_at: latestUpload?.updated_at || latestUpload?.created_at || null,
            retention_expires_at: latestUpload?.retention_expires_at || null,
          });
          setExistingIdentityDocUrl("");
          if (uploadPath) {
            try {
              const url = await signedUrl("id-verifications", uploadPath, 60 * 10);
              if (alive) setExistingIdentityDocUrl(url);
            } catch {
              if (alive) setExistingIdentityDocUrl("");
            }
          }
        }

        const hasHydratedFields = Object.keys(hydratedFields || {}).length > 0;
        if (hasHydratedFields) {
          setOcrSummary({ fields: hydratedFields });
        }
        setHydrationDebug({
          userId: user.id,
          latestUpload: latestUpload
            ? {
                id: latestUpload.id || null,
                status: latestUpload.status || null,
                created_at: latestUpload.created_at || null,
                notes_type: typeof latestUpload.notes,
              }
            : null,
          latestCheck: latestCheck
            ? {
                status: latestCheck.status || null,
                provider: latestCheck.provider || null,
                checked_at: latestCheck.checked_at || null,
              }
            : null,
          latestFields,
          latestCheckFields,
          hydratedFields,
        });

        if (resolveCanonicalKycState(latestStatus, { hasUpload: true }) === "ocr_completed" && (hasHydratedFields || docValue)) {
          setVerifyStatus("passed");
          setVerifyMsg(t("onboarding.idPassport.status.ocrCompleted", "OCR complete. Review pending."));
          setOcrStatus("done");
          setOcrMsg(t("onboarding.idPassport.status.ocrCompleted", "OCR complete. Review pending."));
          return;
        }

        if (latestStatus === "failed") {
          setVerifyStatus("failed");
          setVerifyMsg(t("onboarding.idPassport.status.failed", "Processing Failed"));
          setOcrStatus("failed");
          setOcrMsg(t("onboarding.idPassport.status.failed", "Processing Failed"));
          return;
        }

        if (status === "verified" || hasHydratedFields || docValue) {
          setIdentityVerified(status === "verified" && protectedVerified);
          const completedMessage =
            status === "verified" && protectedVerified
              ? t("onboarding.idPassport.status.verified", "Verified")
              : t("onboarding.idPassport.status.ocrCompleted", "OCR complete. Review pending.");
          setVerifyStatus("passed");
          setVerifyMsg(completedMessage);
          setOcrStatus("done");
          setOcrMsg(completedMessage);
          return;
        }

        if (latestStatus === "pending") {
          setIdentityVerified(false);
          setVerifyStatus("passed");
          setVerifyMsg(
            t(
              "settings.edit.idPassport.awaitingReview",
              "Document received. Awaiting review."
            )
          );
          setOcrStatus("idle");
          setOcrMsg("");
        }
      } catch {
        // non-fatal hydration
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  React.useEffect(() => {
    const uploadId = resolveUploadId(uploadMeta);
    if (!uploadId) return undefined;
    if (activeUploadIdRef.current !== uploadId) return undefined;

    const unsubscribe = subscribeToUploadStatus(uploadId, {
      onUpdate: (row) => {
        const status = String(row?.status || "pending");
        if (resolveCanonicalKycState(status, { hasUpload: true }) === "ocr_completed") {
          setVerifyStatus("passed");
          setOcrStatus("done");
          setIdentityVerified(false);
          setOcrMsg(t("onboarding.idPassport.status.ocrCompleted", "OCR complete. Review pending."));
          setOcrSummary({
            fields: sanitizeIdentityOcrFields(row?.ocrSummary?.fields),
          });
          const nextDocNumber = String(row?.ocrSummary?.fields?.document_number || "")
            .trim()
            .toUpperCase();
          if (nextDocNumber) {
            setValue((prev) => (String(prev || "").trim() ? prev : nextDocNumber));
          }
          return;
        }

        if (status === "failed") {
          setVerifyStatus("failed");
          setOcrStatus("failed");
          setOcrMsg("Processing failed");
          setSnackbar({
            open: true,
            message: t(
              "onboarding.idPassport.uploadFailed",
              "Upload failed. Please try again."
            ),
          });
          return;
        }

        setVerifyStatus("passed");
        setOcrStatus("pending");
        setIdentityVerified(false);
        setOcrMsg(
          t("onboarding.idPassport.processingPending", "Analysing your document...")
        );
      },
      onError: (err) => {
        setVerifyStatus("failed");
        setOcrStatus("failed");
        setSnackbar({
          open: true,
          message:
            err?.message ||
            t(
              "settings.edit.idPassport.statusReadFailed",
              "Could not read verification status."
            ),
        });
      },
    });

    return unsubscribe;
  }, [uploadMeta]);

  async function handleCapture(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setVerifyStatus("failed");
      setVerifyMsg(
        t(
          "onboarding.idPassport.fileTooLarge",
          "File too large. Please upload an image under 5MB."
        )
      );
      setOcrStatus("failed");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    setCaptured(file);
    setBusy(true);

    try {
      setVerifyStatus("pending");
      setVerifyMsg(t("onboarding.idPassport.uploading", "Uploading document..."));
      setOcrStatus("pending");
      setIdentityVerified(false);
      setOcrMsg(
        t("onboarding.idPassport.processingPending", "Analysing your document...")
      );
      setOcrSummary(null);

      const meta = await uploadDocumentForOcr({
        docType: "passport",
        profileTable: "patient_profiles",
        file,
        docNumber: v || "",
        country: "",
        retentionDays: 90,
      });
      activeUploadIdRef.current = resolveUploadId(meta);
      const nextMeta = {
        ...meta,
        status: "pending",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setUploadMeta(nextMeta);
      setExistingIdentityDocUrl("");
      const uploadPath = nextMeta?.path || nextMeta?.storage_path || nextMeta?.key || null;
      if (uploadPath) {
        try {
          const url = await signedUrl("id-verifications", uploadPath, 60 * 10);
          setExistingIdentityDocUrl(url);
        } catch {
          setExistingIdentityDocUrl("");
        }
      }
      setVerifyStatus("passed");
      setVerifyMsg(
        t(
          "onboarding.idPassport.receivedExtracting",
          "Document received. Extracting details..."
        )
      );
    } catch (err) {
      console.error("Upload failed:", err);
      setVerifyStatus("failed");
      setVerifyMsg(
        err?.message ||
          t("onboarding.idPassport.uploadFailed", "Upload failed. Please try again.")
      );
      setOcrStatus("failed");
      setSnackbar({
        open: true,
        message:
          err?.message ||
          t("onboarding.idPassport.uploadFailed", "Upload failed. Please try again."),
      });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleSave() {
    if (!canSave || ocrStatus === "pending" || saving) return;

    try {
      setSaving(true);
      const finalId =
        v ||
        String(ocrSummary?.fields?.document_number || "")
          .trim()
          .toUpperCase();

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (authErr) throw authErr;
      if (!user) {
        throw new Error(
          t("settings.edit.idPassport.signInRequired", "You need to be signed in.")
        );
      }

      const nowIso = new Date().toISOString();

      const { error: saveErr } = await supabase
        .from("patient_profiles")
        .upsert(
          {
            user_id: user.id,
            national_id: finalId || null,
            national_id_number: finalId || null,
            verification_status: "pending",
            updated_at: nowIso,
          },
          { onConflict: "user_id" }
        );

      if (saveErr) throw saveErr;

      try {
        await auth?.refreshProfile?.();
        localStorage.setItem("afrimed:profile:refresh", String(Date.now()));
      } catch {}

      // 1) Local intro preview → mark as pending
      const current = loadPatientIntroPreview() || {};
      const nextPreview = {
        ...current,
        nationalId: finalId,
        idVerified: false,
        idPending: true,
      };
      savePatientIntroPreview(nextPreview);

      setSnackbar({
        open: true,
        message: t(
          "settings.edit.idPassport.submitted",
          "ID / Passport submitted for verification"
        ),
      });

      // Let the snackbar show, then return to EditIntro
      setTimeout(() => {
        nav(PATIENT_SETTINGS_PROFILE_ROUTE);
      }, 2200);
    } catch (e) {
      console.error("[EditIdPassport] save error:", e);
      setSnackbar({
        open: true,
        message:
          e?.message ||
          t(
            "settings.edit.common.saveFailed",
            "Could not save changes. Please try again."
          ),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <SettingsScreenShell>
      <TopBar
        title={t("onboarding.idPassport.title", "National ID • Passport")}
        subtitle={t(
          "onboarding.idPassport.subtitle",
          "Upload your document to auto-fill, or enter your ID/Passport number manually."
        )}
        onBack={() => nav(PATIENT_SETTINGS_PROFILE_ROUTE)}
        trailing={<SaveChip disabled={!canSave || ocrStatus === "pending" || saving} onClick={handleSave} />}
      />

      <div className="mt-5">
        <FolaOnboardingCard role="patient" />
      </div>

      <div className="mt-5 md:mt-6 space-y-4">
        <p className="text-body-small text-on-surface-var">
          {t(
            "onboarding.idPassport.securityCopy",
            "AfriMed™ uses Supabase infrastructure with TLS encryption in transit and AES-256 encryption at rest. Supabase maintains SOC 2 Type 2 and ISO 27001 controls. Your information remains pending until AfriMed™ completes protected verification review."
          )}
        </p>

        {/* Input with tick */}
        <div className="relative">
          <TextField56
            label={t("onboarding.idPassport.label", "ID / Passport")}
            value={value}
            onChange={handleChange}
            placeholder={t(
              "onboarding.idPassport.placeholder",
              "A12345678  ·  or MRZ line"
            )}
            inputMode="text"
            tone="surface"
            clearable={false}
          />
          {showVerified && (
            <img
              src="/icons/check-done.svg"
              alt=""
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5"
            />
          )}
        </div>

        {/* Helper / inline error */}
        {!valid && value && ocrStatus !== "done" && (
          <p className="mt-1 text-sm text-[rgb(var(--afrimed-danger))]">
            {t(
              "onboarding.idPassport.invalidHint",
              "Use a passport number or paste a 30-character MRZ line."
            )}
          </p>
        )}

        <div className="mt-4 space-y-3">
          <div className="flex flex-col">
            <span className="text-label-medium text-on-surface">
              {t("onboarding.idPassport.processingTitle", "ID Processing")}
            </span>
            <span className="text-body-small text-on-surface-var">
              {ocrStatus === "pending"
                ? t(
                    "onboarding.idPassport.processingPending",
                    "Analysing your document..."
                  )
                : t(
                    "onboarding.idPassport.processingIdle",
                    "Capture a photo or upload a clear scan"
                  )}
            </span>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || ocrStatus === "pending"}
              className="h-10 flex-1 rounded-[10px] bg-[rgba(77,163,255,0.12)] text-[rgb(var(--afrimed-primary))] disabled:opacity-60"
              onClick={() => {
                setPendingAction("camera");
                setConsentOpen(true);
              }}
            >
              {ocrStatus === "pending"
                ? t("onboarding.idPassport.working", "Working...")
                : captured
                  ? t("onboarding.idPassport.retake", "Retake")
                  : t("onboarding.idPassport.camera", "Camera")}
            </button>

            <button
              type="button"
              disabled={busy || ocrStatus === "pending"}
              className="h-10 flex-1 rounded-[10px] bg-[rgba(77,163,255,0.12)] text-[rgb(var(--afrimed-primary))] hover:bg-[rgba(77,163,255,0.18)] disabled:opacity-60"
              onClick={() => {
                setPendingAction("file");
                setConsentOpen(true);
              }}
            >
              {t("onboarding.idPassport.uploadFile", "Upload File")}
            </button>
          </div>
        </div>

        <ConsentModal
          open={consentOpen}
          onCancel={() => {
            setConsentOpen(false);
            setPendingAction(null);
          }}
          onAccept={() => {
            setConsentOpen(false);
            if (pendingAction === "camera") {
              fileRef.current?.setAttribute("capture", "environment");
            } else {
              fileRef.current?.removeAttribute("capture");
            }
            triggerCapture();
            setPendingAction(null);
          }}
        />

        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={handleCapture}
        />

        {uploadMeta?.path || uploadMeta?.storage_path || uploadMeta?.key ? (
          <div className="mt-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="truncate text-label-large text-on-surface">
                  {t("settings.edit.idPassport.visibility.identityDocument", "ID / Passport document")}
                </div>
                <div className="mt-1 text-body-small text-on-surface-var">
                  {uploadMeta?.updated_at || uploadMeta?.created_at
                    ? t("settings.edit.idPassport.visibility.uploadedDate", "Uploaded {date}", {
                        date: formatDate(uploadMeta.updated_at || uploadMeta.created_at, {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        }),
                      })
                    : t("settings.edit.idPassport.visibility.received", "Received by AfriMed™")}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={identityDocumentStateClass(identityDocumentChipState)}>
                  {identityDocumentStateLabel(identityDocumentChipState)}
                </span>
                {existingIdentityDocUrl ? (
                  <a
                    href={existingIdentityDocUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-9 items-center rounded-[10px] bg-[rgba(77,163,255,0.12)] px-3 text-label-medium text-primary"
                  >
                    {t("settings.edit.idPassport.visibility.previewOpen", "Preview • Open")}
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {(ocrStatus !== "idle" || verifyStatus !== "idle") && (
          <IdentityStatusCard
            uiState={uiState}
            statusTitle={statusTitle}
            message={ocrStatus === "pending" ? ocrMsg || statusTitle : verifyMsg || statusTitle}
            fields={
              Object.keys(ocrFields).length > 0
                ? {
                    surname: toOfficialText(ocrSurname) || "",
                    document_number: toOfficialText(ocrDocNumber) || "",
                    date_of_birth: ocrDobDisplay || "",
                    expiry_date: ocrExpiryDisplay || "",
                    given_names: toOfficialText(ocrGivenNames) || "",
                  }
                : null
            }
            panelTone={panelTone}
            t={t}
          />
        )}

        {uploadMeta?.retention_expires_at && (
          <p className="mt-4 text-[10px] text-on-surface-var text-center italic">
            {t(
              "onboarding.idPassport.retention",
              "Privacy: Encrypted storage expires {date}.",
              {
                date: formatDate(uploadMeta.retention_expires_at, {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                }),
              }
            )}
          </p>
        )}
      </div>

      <Snackbar
        open={snackbar.open}
        onClose={() =>
          setSnackbar((s) => ({
            ...s,
            open: false,
          }))
        }
        duration={2200}
      >
        {snackbar.message}
      </Snackbar>
    </SettingsScreenShell>
  );
}
